/**
 * Four clocks, for the four places the fleet is scheduled around.
 *
 * Every time on this screen is shown in one zone -- whichever is picked at the
 * top of Team Tools -- which is right for entering a time and wrong for
 * deciding one. "Build it at 18:00 Eastern" is a decision about whether anyone
 * in Europe or South East Asia is awake, and that arithmetic was being done in
 * people's heads against a single converted figure.
 *
 * ZONES, NOT OFFSETS. `America/New_York` rather than UTC-5, so the clocks are
 * right across a daylight-saving change without anybody editing them -- and so
 * the label can say EDT in July and EST in January, which is the thing that
 * actually catches people out. Europe and South East Asia get one zone each
 * because the fleet has one slot range each; a picker of every city would be a
 * different screen.
 *
 * It ticks once a second from the device clock. No request, and nothing here
 * is read by anything else -- if the device clock is wrong the clocks are
 * wrong, which is true of every clock the person is already looking at.
 */
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

type Face = { id: string; label: string; place: string }

/** Eastern and Pacific because the team schedules in them; Berlin and
 *  Singapore because that is where the EU and SEA slot ranges are. */
const FACES: Face[] = [
    { id: 'America/New_York', label: 'EST', place: 'New York' },
    { id: 'America/Los_Angeles', label: 'PST', place: 'Los Angeles' },
    { id: 'Europe/Berlin', label: 'EU', place: 'Berlin' },
    { id: 'Asia/Singapore', label: 'SEA', place: 'Singapore' },
]

/** The zone's own abbreviation right now -- EDT in July, EST in January.
 *  Falls back to the fixed label when the browser will not name it. */
const shortName = (zone: string, fallback: string) => {
    try {
        const part = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'short',
        })
            .formatToParts(new Date())
            .find((p) => p.type === 'timeZoneName')
        return part?.value && !part.value.startsWith('GMT') ? part.value : fallback
    } catch {
        return fallback
    }
}

const Clock = ({ face, at }: { face: Face; at: Date }) => {
    // 12-hour with the am/pm, not 24-hour. These are read at a glance beside
    // each other, and "17:25" is a conversion before it is a time.
    const time = at.toLocaleTimeString('en-US', {
        timeZone: face.id,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
    /* The day on every clock, highlighted when it is not the reader's.
     *
     * It was shown only on the odd one out, which reads as a warning badge
     * rather than as information -- and leaves the other three looking like
     * they have no day at all. Every clock says which day it is; the ones that
     * disagree with you say it in amber. */
    const theirDay = at.toLocaleDateString([], { timeZone: face.id, weekday: 'short' })
    const myDay = at.toLocaleDateString([], { weekday: 'short' })

    return (
        <div
            className='flex flex-col rounded border border-white/15 bg-white/[0.03] px-3 py-2'
            title={`${face.place} — ${shortName(face.id, face.label)}`}
        >
            <span className='flex flex-row items-baseline gap-2'>
                <span className='text-[10px] uppercase tracking-widest text-white/50'>
                    {face.label}
                </span>
                <span
                    className={twMerge(
                        'text-[10px]',
                        theirDay === myDay ? 'text-white/40' : 'text-amber-300'
                    )}
                    title={theirDay === myDay ? undefined : 'A different day where you are'}
                >
                    {theirDay}
                </span>
            </span>
            <span className='font-mono text-2xl font-bold tabular-nums tracking-tight text-white'>
                {time.replace(/\s?([AP]M)$/i, '')}
                <span className='pl-1 text-xs font-normal text-white/50'>
                    {(time.match(/([AP]M)$/i) || [''])[0].toLowerCase()}
                </span>
            </span>
            <span className='text-[10px] text-white/30'>{face.place}</span>
        </div>
    )
}

export const WorldClocks = () => {
    const [at, setAt] = useState(() => new Date())

    useEffect(() => {
        // Aligned to the next whole second, so all four tick together rather
        // than drifting a fraction apart from whenever the page happened to
        // load.
        const toNextSecond = 1000 - (Date.now() % 1000)
        let every: ReturnType<typeof setInterval>
        const align = setTimeout(() => {
            setAt(new Date())
            every = setInterval(() => setAt(new Date()), 1000)
        }, toNextSecond)
        return () => {
            clearTimeout(align)
            clearInterval(every)
        }
    }, [])

    return (
        <div className='flex flex-row flex-wrap gap-2 lg:flex-col'>
            {FACES.map((face) => (
                <Clock key={face.id} face={face} at={at} />
            ))}
        </div>
    )
}
